import { z } from 'zod'

import {
  accessModeSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  platformRoleSchema,
  searchQuerySchema,
  slugSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * P5–P7, P16 · Opening a trader's book.
 *
 * This is the most sensitive thing the platform can do, and the contract is
 * shaped so that it cannot be done quietly.
 *
 * **`reason` is required and must be non-empty.** Not a nicety: `access_log`
 * is immutable and every entry into a book carries the reason, the ticket if
 * there was one, how long it lasted and how many edits were made. An empty or
 * whitespace reason is a `400`, because "support opened your book, no reason
 * given" is the row nobody should be able to write.
 *
 * A grant lasts an hour, and while it is live **every trader endpoint works
 * unchanged** with the tenant taken from the grant — which is why there is no
 * parallel copy of the trader API for the console, and why `withPlatformAccess()`
 * must be the only way in.
 */
export const openAccessRequest = z.object({
  /**
   * Required, non-empty after trimming, and long enough to mean something.
   * "asked to check his wanda bill for August" — not "x".
   */
  reason: z.string().trim().min(3, 'Say why you are opening this book.').max(500),
  mode: accessModeSchema.default('READ_ONLY'),
  ticketId: idSchema.optional(),
})

export const accessGrantResponse = z.object({
  accessId: idSchema,
  tenantId: idSchema,
  tenantSlug: slugSchema,
  tenantName: z.string(),
  mode: accessModeSchema,
  /** A scoped session, one hour. Bearer for the console's API calls. */
  token: z.string().min(1).optional(),
  openedAt: isoInstantSchema,
  expiresAt: isoInstantSchema,
  reason: z.string(),
  ticketId: idSchema.nullish(),
})

/* ── P6 · POST /platform/access/:accessId/mode ────────────────────────────── */

/**
 * "Switch to edit" (screen 06). Escalating to `EDIT` is itself recorded, and
 * every write made afterwards appears in the **trader's own** audit log as a
 * platform action — the owner sees what support changed in his book without
 * asking anyone.
 */
export const changeAccessModeRequest = z.object({
  mode: accessModeSchema,
  reason: z.string().trim().min(3).max(500).optional(),
})

/* ── P7 · POST /platform/access/:accessId/close ───────────────────────────── */

export const closeAccessResponse = z.object({
  accessId: idSchema,
  closedAt: isoInstantSchema,
  durationSeconds: z.number().int().nonnegative(),
  editsCount: z.number().int().nonnegative(),
})

/* ── P16 · GET /platform/access-log ───────────────────────────────────────── */

/**
 * **Read-only. No mutating verb exists on this route** — not for support, not
 * for the platform owner. The table has no `deleted_at` and the database
 * revokes UPDATE and DELETE from the application role.
 */
export const accessLogEntrySchema = z.object({
  id: idSchema,
  openedAt: isoInstantSchema,
  actorUserId: idSchema,
  actorName: z.string(),
  actorRole: platformRoleSchema,
  tenantId: idSchema,
  tenantName: z.string(),
  tenantSlug: slugSchema,
  reason: z.string(),
  ticketId: idSchema.nullish(),
  mode: accessModeSchema,
  closedAt: isoInstantSchema.nullish(),
  durationSeconds: z.number().int().nonnegative().nullish(),
  editsCount: z.number().int().nonnegative(),
  ip: z.string().nullish(),
})

export const accessLogQuery = cursorQuerySchema.extend({
  /** Only sessions that changed something. */
  edited: z.coerce.boolean().optional(),
  /** Only sessions opened without a ticket — the row that needs explaining. */
  noTicket: z.coerce.boolean().optional(),
  q: searchQuerySchema.optional(),
  tenantId: idSchema.optional(),
  actorUserId: idSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
})

export const accessLogResponse = z.object({
  booksOpened: z.number().int().nonnegative(),
  editedCount: z.number().int().nonnegative(),
  averageMinutes: z.number().nonnegative(),
  ownersNotified: z.number().int().nonnegative(),
  items: z.array(accessLogEntrySchema),
})

export type OpenAccessRequest = z.input<typeof openAccessRequest>
export type AccessGrantResponse = z.infer<typeof accessGrantResponse>
export type ChangeAccessModeRequest = z.input<typeof changeAccessModeRequest>
export type CloseAccessResponse = z.infer<typeof closeAccessResponse>
export type AccessLogEntry = z.infer<typeof accessLogEntrySchema>
export type AccessLogQuery = z.input<typeof accessLogQuery>
export type AccessLogResponse = z.infer<typeof accessLogResponse>
